import React, { useState, useMemo, useRef } from 'react'
import './shop-import.scss'
import useBemClass from '@/utils/hooks/useBEMClass'
import { Column, Row, Expaned } from '@/components/layout/layout'
import SearchInput from '@/components/search-input/search-input'
import { Dropdown, Menu, Spin, notification } from 'antd'
import Iconfont from '@/components/iconfont/iconfont'
import { useRequest, useCountDown } from 'ahooks'
import { $post } from '@/api'
import $msg from '@/utils/message'
import Button from '@/components/button/button'
import classNames from 'classnames'
import Input from '@/components/input/input'
import { showLayer, layerManage } from '@/utils/layer'
import ShopImportTip from './shop-import-tip'
import ImportIng from '@/assets/images/import-ing.svg'
import ImportFinish from '@/assets/images/import-finish.svg'

interface ILSShopInfo {
    encrypted_shop_id?: string
    shop_name?: string
    shop_trademark_url?: string
    super_admin_imgurl?: string
    super_admin_nickname?: string
    super_admin_phone?: string
    super_admin_realname?: string
    super_admin_sex?: number
}

export interface ICheckGoodRes {
    duplicated_goods_count: number
    goods_names: string[]
    total_goods_count: number
}

interface ShopImportProps {}

const ShopImport: React.FC<ShopImportProps> = props => {
    const cls = useBemClass('shop-import')

    const [current, setCurrent] = useState<number>(0)
    const [shopInfo, setShopInfo] = useState<ILSShopInfo>({})
    const [shopId, setShopId] = useState<string>('')
    const [code, setCode] = useState<string>('') // 短信验证码
    const [isError, setIsError] = useState<boolean>(false)

    const oldShopId = useRef<string>('')

    const [countdownStatus, setCountdownStatus] = useState<number>(0) // 0 未发送过 1 倒计时中 // 2 已发送过，重新发送

    const [countdown, setTargetDate] = useCountDown({
        onEnd: () => {
            setCountdownStatus(2)
        },
    })

    const searchReq = useRequest(
        (val: string) =>
            $post({
                url: '/goods/migration/getlsshopinfo',
                params: {
                    shop_code: val,
                },
            }).then(res => {
                if (Object.keys(res.shop_info).length) setShopId(val)
                setShopInfo(res.shop_info)
            }),
        {
            manual: true,
            onError: e => $msg('error', e.message),
        },
    )

    const msgValidateReq = useRequest(
        () =>
            $post({
                url: '/goods/migration/sendcaptchatolsshopsuperadmin',
                params: {
                    shop_code: shopId,
                },
            }),
        {
            manual: true,
            onSuccess: () => {
                setTargetDate(Date.now() + 60 * 1000)
                setCountdownStatus(1)
            },
        },
    )

    const hasShopInfo = useMemo(() => !!Object.keys(shopInfo).length, [shopInfo])

    const impotGoodReq = useRequest(
        (isReplace: boolean) =>
            $post({
                url: '/goods/migration/importlsgoods',
                params: {
                    encrypted_old_ls_shop_id: oldShopId.current,
                    replace_existing: isReplace,
                },
            }),
        {
            manual: true,
            onSuccess: () => {
                layerManage.pop()
                notification.open({
                    icon: <img src={ImportIng} data-margin-top-10 alt="" />,
                    style: {
                        borderRadius: '10px',
                    },
                    key: 'import-ing',
                    message: (
                        <div data-color-000 data-font-size-20 data-font-weight-600>
                            批量商品导入中
                        </div>
                    ),
                    description: (
                        <div data-color-000 data-font-size-14>
                            请勿关闭网页/断开网络
                        </div>
                    ),
                    duration: null,
                })
            },
            onError: e => $msg('error', e.message),
        },
    )

    const checkImportGood = useRequest(
        () =>
            $post({
                url: '/goods/migration/getduplicatedlsgoodsnames',
                params: {
                    encrypted_old_ls_shop_id: oldShopId.current,
                },
            }),
        {
            manual: true,
            onSuccess: (res: ICheckGoodRes) => {
                if (res.duplicated_goods_count) {
                    showLayer(
                        <ShopImportTip
                            {...res}
                            onConfirm={val => {
                                impotGoodReq.run(val)
                            }}
                        />,
                    )
                } else {
                    impotGoodReq.run(false)
                }
            },
        },
    )

    const handleImport = useRequest(
        () =>
            $post({
                url: '/goods/migration/verifylsshopcaptcha',
                params: {
                    code,
                    shop_code: shopId,
                },
            }),
        {
            manual: true,
            onSuccess: res => {
                isError && setIsError(false)
                oldShopId.current = res.encrypted_old_ls_shop_id
                checkImportGood.run()
            },
            onError: () => {
                setIsError(true)
            },
        },
    )

    return (
        <Column data-width-100p data-align-items-center>
            <div className={cls('tip')}>
                <Row data-margin-bottom-16>
                    <div>1.</div>
                    <div data-margin-left-8>仅限从森果老零售店铺导入，可帮您在门店零售店铺中快速新建商品。</div>
                </Row>
                <Row data-margin-bottom-16>
                    <div>2.</div>
                    <Column data-margin-left-8>
                        <Row data-align-items-center>
                            <div>选中需导入的商品卡片，将会自动为您导入：</div>
                            <div data-font-weight-600>商品分组、商品名称、商品图片、进价、零售价、阶梯</div>
                        </Row>
                        <div data-font-weight-600>
                            价、会员价（门店零售中会员价无会员等级限制）、单位、商品库存、商品编码、商品条码。
                        </div>
                    </Column>
                </Row>
                <Row data-margin-bottom-16>
                    <div>3.</div>
                    <div data-margin-left-8 data-margin-right-12>
                        商品数据导入并不意味店铺之间数据互通，导入后两店铺商品销售仍然相互独立，营收数据不会互相影响。
                    </div>
                </Row>
                <Row data-margin-bottom-16>
                    <div>4.</div>
                    <div data-margin-left-8>如导入的商品名与店铺已存商品同名，将会自动创建新的同名商品。</div>
                </Row>
            </div>
            <div data-row>
                <div className={cls('step', current === 0 ? 'active' : 'unactive')}>
                    {current === 0 ? (
                        <div className="circle"></div>
                    ) : (
                        <img data-height-17 data-width-17 src={ImportFinish} alt="" />
                    )}
                    <div
                        className="txt"
                        style={{
                            color: current === 0 ? '#000' : '#14CC9B',
                        }}
                    >
                        输入店铺号
                    </div>
                    <div className={cls('step', 'line')}></div>
                </div>
                <div data-margin-left-60 className={cls('step', current === 1 ? 'active' : 'unactive')}>
                    <div className="circle"></div>
                    <div className="txt">超管验证</div>
                </div>
            </div>
            {current === 0 && (
                <div className={cls('search')}>
                    <Expaned data-margin-right-16>
                        <SearchInput
                            isExpaned
                            searchDom={
                                <Dropdown
                                    overlay={
                                        <Menu>
                                            <Menu.Item disabled={!hasShopInfo}>
                                                <Spin spinning={searchReq.loading}>
                                                    <div
                                                        className={cls('search', 'item')}
                                                        onClick={() => {
                                                            if (hasShopInfo) {
                                                                setCurrent(1)
                                                            }
                                                        }}
                                                    >
                                                        {hasShopInfo ? (
                                                            <>
                                                                <Row data-align-items-center>
                                                                    <div className={cls('search', 'tag')}>门店零售</div>
                                                                    <div className={cls('search', 'title')}>
                                                                        {shopInfo?.shop_name}
                                                                    </div>
                                                                </Row>
                                                                <div className="action">选择</div>
                                                            </>
                                                        ) : (
                                                            '暂无搜索结果'
                                                        )}
                                                    </div>
                                                </Spin>
                                            </Menu.Item>
                                        </Menu>
                                    }
                                    trigger={['click']}
                                >
                                    <div className={cls('search', 'action')} />
                                </Dropdown>
                            }
                            placeholder="请输入老零售店铺号"
                            onSearch={val => {
                                searchReq.run(val)
                            }}
                        />
                    </Expaned>
                    <Iconfont name="icon8yiwen-16" size={24} />
                </div>
            )}
            {current === 1 && (
                <Column data-width-100p data-align-items-center>
                    <div className={cls('validate', 'title')}>{shopInfo?.shop_name}</div>
                    <div className={cls('validate', 'des')}>店铺号: {shopId}</div>
                    <div className={cls('validate', 'container')}>
                        <div className={cls('user')}>
                            <img
                                src={shopInfo?.super_admin_imgurl}
                                className={classNames(cls('avatar'), cls('avatar', shopInfo?.super_admin_sex + ''))}
                                alt=""
                            />
                            <div className={cls('info')}>
                                <span className={cls('info', 'nickname')}>{shopInfo?.super_admin_nickname}</span>
                                <span className={cls('info', 'phone')}>{shopInfo?.super_admin_phone}</span>
                            </div>
                        </div>
                        {countdownStatus === 0 ? (
                            <Button
                                types="default"
                                onClick={() => {
                                    msgValidateReq.run()
                                }}
                            >
                                发送验证码
                            </Button>
                        ) : (
                            <div className={cls('ipt')}>
                                {isError && <div className={cls('ipt', 'error')}>验证码错误，请重新输入</div>}
                                <Input
                                    onChange={val => {
                                        setCode(val.target.value)
                                    }}
                                    error={isError}
                                    maxLength={4}
                                    inputsuffix={() => (
                                        <div
                                            className={cls('ipt', 'suffix')}
                                            style={{
                                                color: countdownStatus === 1 ? '#999' : '#14CC9B',
                                                cursor: countdownStatus === 1 ? 'not-allowed' : 'pointer',
                                            }}
                                            onClick={() => {
                                                msgValidateReq.run()
                                            }}
                                            data-font-size-14
                                        >
                                            {countdownStatus === 1
                                                ? `已发送(${Math.round(countdown / 1000)})`
                                                : '重新发送'}
                                        </div>
                                    )}
                                />
                            </div>
                        )}
                    </div>
                    <Row data-margin-top-32>
                        <Button
                            types="text"
                            style={{
                                color: '#999',
                                marginRight: '16px',
                                fontWeight: 600,
                            }}
                            onClick={() => {
                                setCurrent(0)
                            }}
                        >
                            上一步
                        </Button>
                        <Button
                            onClick={handleImport.run}
                            loading={impotGoodReq.loading || handleImport.loading || checkImportGood.loading}
                            disabled={countdownStatus === 0 || code.length < 4}
                            types="default"
                        >
                            {impotGoodReq.loading ? '导入中' : '开始导入'}
                        </Button>
                    </Row>
                </Column>
            )}
        </Column>
    )
}

export default ShopImport
